<template>
  <v-container class="remainder-of-estate-container standard-content-container">
    <DynamicNavDrawer
      :drawerOpen="readMoreDrawerOpen"
      @drawerClosed="drawerClosed"
      :title="'Key considerations for inheritance choices'"
      :description="'Considerations when creating your will'"
    >
      <template v-slot:content>
        <Accordion
          :accordionData="inheritanceChoicesData"
          :html="true"
          :isNavDrawer="true"
        />
      </template>
    </DynamicNavDrawer>
    <v-row no-gutters>
      <v-col cols="12" md="5">
        <v-row no-gutters class="title-section">
          <v-col
            cols="12"
            sm="2"
            class="link-container"
            :class="{
              'small-container':
                this.$vuetify.breakpoint.width <=
                this.$vuetify.breakpoint.thresholds.xs,
            }"
          >
            <router-link
              :to="{ name: 'OverviewPage', hash: '#remainder-of-estate' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="10" class="">
            <h1>Remainder of Estate</h1>
            <p>
              The remainder of your estate refers to your assets which are left
              over, after specific bequests have been made. Should you leave no
              specific bequests to any person or charity, the remainder of your
              estate will be your entire estate.
            </p>
            <p>You have the option to:</p>
            <p>
              &bull; Leave the remainder to your spouse<br />
              <u><strong>or</strong></u
              ><br />
              &bull; Allocate percentages to chosen individuals and/or charities
              and/or trusts<br />
              <u><strong>or</strong></u
              ><br />
              &bull; Distribute the remainder according to Islamic Sharia law
            </p>
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <AdditionalInfoComponent
                v-if="inheritanceChoicesVideo"
                :heading="'Key considerations for inheritance choices'"
                :source="inheritanceChoicesVideo"
                @drawerOpen="drawerOpen()"
              ></AdditionalInfoComponent>
            </template>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="7">
        <div class="left-section-container">
          <v-form @submit.prevent="submit">
            <h2>
              How do you want the remainder of your estate to be distributed?
            </h2>
            <p v-if="testator.isMarried || testator.hasChildren">
              I want the remainder of my estate to go to:
            </p>
            <RadioSelect
              v-if="whitelabel.omDesign"
              v-model="distribution"
              :items="distributionItems"
              :color="'white'"
              :group="'distribution'"
              :flexColumn="true"
            />
            <v-radio-group v-else v-model="distribution">
              <v-radio
                v-if="testator.isMarried"
                :label="spouseLabel"
                value="SPOUSE"
                color="primary"
              />
              <p v-if="testator.isMarried">or...</p>
              <v-radio
                :label="specificPercentagesLabel"
                value="SPECIFIC_PERCENTAGES"
                color="primary"
              />
              <p>or...</p>
              <v-radio
                label="I want the remainder of my estate to devolve in accordance with Islamic Sharia law"
                value="SHARIA_LAW"
                color="primary"
              />
            </v-radio-group>
            <div class="button-container">
              <GradientButton
                v-if="distribution"
                :title="'Next'"
                :small="true"
                :callout="'right'"
                :loading="buttonLoading"
                type="submit"
              />
            </div>
          </v-form>
          <template v-if="$vuetify.breakpoint.smAndDown">
            <AdditionalInfoComponent
              class="additional-info-mobile"
              v-if="inheritanceChoicesVideo"
              :heading="'Key considerations for inheritance choices'"
              :source="inheritanceChoicesVideo"
              @drawerOpen="drawerOpen()"
            ></AdditionalInfoComponent>
          </template>
        </div>
      </v-col>
    </v-row>

    <ErrorSnackbar v-model="showError" />
  </v-container>
</template>

<script>
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import {
    CREATE_REMAINDER_OF_ESTATE_MUTATION,
    UPDATE_REMAINDER_OF_ESTATE_MUTATION,
  } from '../../constants/graphql/remainder-of-estates';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { getRelations } from '../../constants/js/people';
  import DynamicNavDrawer from '../guest/DynamicNavDrawer.vue';
  import Accordion from '../common/Accordion.vue';
  import AdditionalInfoComponent from '../../components/people/AdditionalInfoComponent.vue';

  export default {
    name: 'RemainderOfEstateDistribution',
    components: {
      ErrorSnackbar,
      DynamicNavDrawer,
      Accordion,
      AdditionalInfoComponent,
    },
    mixins: [formHelperMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      let distribution = this.testator.remainderOfEstate
        ? this.testator.remainderOfEstate.distribution
        : null;

      if (
        distribution === 'CHILDREN' ||
        distribution === 'SPOUSE_AND_CHILDREN'
      ) {
        // We're ignoring legacy options to force the user to go through the remainder of estate journey again to select correct options.
        distribution = null;
      }
      return {
        distribution: distribution,
        inheritanceChoicesVideo: null,
        inheritanceChoicesData: [],
        readMoreDrawerOpen: false,
      };
    },
    mounted() {
      this.inheritanceChoicesVideo =
        this.whitelabel.videos.inheritanceChoicesVideo;
      this.inheritanceChoicesData = this.whitelabel.inheritanceChoicesData;
    },
    computed: {
      isMobile() {
        return this.$isMobileDevice();
      },
      isUpdate() {
        return this.testator.remainderOfEstate;
      },
      spouseLabel() {
        const relations = getRelations(this.testator.people);
        return relations.spouse.fullName;
      },
      spouseAndChildrenLabel() {
        return `${this.spouseLabel} and children in equal shares`;
      },
      specificPercentagesLabel() {
        if (this.testator.willCountry == 'GB') {
          return 'I want to set specific percentages to be distributed to specific people and/or charities';
        } else {
          return 'I want to set specific percentages to be distributed to specific people, charities and/or trusts';
        }
      },
      distributionItems() {
        const items = [];

        if (this.testator.isMarried) {
          items.push({
            value: 'SPOUSE',
            text: this.spouseLabel,
          });
        }

        items.push({
          value: 'SPECIFIC_PERCENTAGES',
          text: this.specificPercentagesLabel,
        });

        items.push({
          value: 'SHARIA_LAW',
          text: 'I want the remainder of my estate to devolve in accordance with Islamic Sharia law',
        });

        return items;
      },
    },
    methods: {
      submit() {
        this.buttonLoading = true;
        this.isUpdate
          ? this.updateRemainderOfEstate()
          : this.createRemainderOfEstate();
      },
      createRemainderOfEstate() {
        const remainderOfEstateInputDetails = {
          distribution: this.distribution,
          testatorId: this.testator.id,
        };
        this.$apollo
          .mutate({
            mutation: CREATE_REMAINDER_OF_ESTATE_MUTATION,
            variables: {
              remainderOfEstateInputDetails,
            },
            update: (cache, { data: { createRemainderOfEstate } }) => {
              this.updateCache(cache, createRemainderOfEstate);
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateRemainderOfEstate() {
        const remainderOfEstateInputDetails = {
          distribution: this.distribution,
        };
        const id = this.testator.remainderOfEstate.id;
        this.$apollo
          .mutate({
            mutation: UPDATE_REMAINDER_OF_ESTATE_MUTATION,
            variables: {
              id,
              remainderOfEstateInputDetails,
            },
            update: (cache, { data: { updateRemainderOfEstate } }) => {
              this.updateCache(cache, updateRemainderOfEstate);
            },
          })
          .then(_response => {
            this.redirectToNext();
            this.buttonLoading = false;
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      updateCache(cache, remainderOfEstate) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testator.id },
        };
        const data = cache.readQuery(query);

        data.testator.remainderOfEstate = remainderOfEstate;

        cache.writeQuery({ ...query, data: data });
      },
      redirectToNext() {
        if (this.distribution == 'SPECIFIC_PERCENTAGES') {
          this.$router.push('distribution-specific-percentages');
        } else if (this.distribution == 'SHARIA_LAW') {
          this.$router.push({
            name: 'OverviewPage',
            hash: '#remainder-of-estate',
          });
        } else {
          this.$router.push('alternative-distribution');
        }
      },
      drawerOpen() {
        this.readMoreDrawerOpen = true;
      },
      drawerClosed() {
        this.readMoreDrawerOpen = false;
      },
    },
  };
</script>
<style scoped lang="scss">
  .remainder-of-estate-container.small-distr-container {
    padding: 0;
    padding-top: 28px;
    padding-bottom: 48px;
  }

  .remainder-of-estate-container {
    padding-top: 28px;

    .left-section-container {
      h2 {
        font-family: Montserrat;
        font-size: 24px;
        font-weight: 700;
        line-height: 28px;
        text-align: left;
        color: #282828;
        margin-bottom: 20px;
      }
    }

    .remainder-title {
      line-height: 36px;
    }

    .additional-info-mobile {
      margin-bottom: 60px;
    }

    .header-row {
      padding-bottom: 40px;
      display: flex;
      gap: 76px;
      h1 {
        flex: 1;
      }
      .specific-donations-description {
        max-width: 331px;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        color: #282828;
        padding-top: 20px;
        max-width: 331px;
      }
    }

    .button-container {
      align-items: left;
      width: 100%;
    }
  }

  @media only screen and (min-width: 600px) and (max-width: 959px) {
    .remainder-of-estate-container.standard-content-container {
      .left-section-container {
        padding-left: 79px;
      }
    }
  }
</style>
